/**
 * THE TYPED CLIENT LAYER FOR THE EDITOR SURFACE — the twenty operations `CabinetApi` does not hold.
 *
 * WHY A SECOND INTERFACE RATHER THAN TWENTY MORE METHODS ON `CabinetApi`. ./cabinet-api.ts is the
 * list-and-read surface every other cabinet screen shares, and it is the file the dashboard, the
 * comments inbox and the payments screens all import. The editor is one screen with its own
 * lifecycle — a working copy, a revision counter, a conflict path and a review record — and none of
 * it is reachable from any other screen. Keeping it here means the seam stays one file per surface
 * and the editor's twenty operations cannot appear in `CABINET_OPERATIONS_COVERED` by accident.
 *
 * ★★ EVERY SHAPE BELOW IS DERIVED THROUGH ./generated, AND NOTHING HERE REACHES THE GENERATED TYPES
 * DIRECTLY. `generated.ts` calls itself "THE ONLY FILE UNDER web/authors THAT REACHES THE
 * GENERATED TYPES", and that remains true: `OperationResult`, `OperationQuery` and `OperationBody`
 * are re-exported from it and every alias below resolves through one of the three. An operation the
 * spec does not declare cannot be named here, which is what makes "that operation does not exist" a
 * compile error rather than a 404 at the author's desk.
 *
 * ★ TWO SHAPES THE THREE HELPERS CANNOT REACH, and how they are handled instead of invented:
 *
 *   1. `submitArticle` and `resubmitArticle` answer **202**, not 200 or 201
 *      (authors-api.d.ts and :4500), and `OperationResult` branches on 200 then 201 and
 *      otherwise gives `void` (generated.ts). Both 202 bodies are declared as
 *      `TransitionResult`, which ./generated already re-exports and which every other lifecycle
 *      move on this app already returns — so the return type below is that alias, and the missing
 *      `OperationAccepted` helper is filed for the owner of generated.ts rather than worked around
 *      with a hand-written body shape.
 *   2. The two problem bodies that carry an EXTENSION MEMBER — `AutosaveConflictProblem.current`
 *      (the server's whole working copy) and `StructuralLimitsProblem.failures` (the whole failure
 *      set) — hang off `components['responses']`, which ./generated does not re-export. `ApiProblem`
 *      keeps only the five RFC members (problem.ts), so neither extension survives the throw
 *      at all. The transports below read them off the decoded payload before throwing and attach
 *      them to the two subclasses at the bottom of this file. A screen branches on the CLASS, never
 *      on a cast.
 *
 * ERRORS ARE THROWN, exactly as on ./cabinet-api.ts: `ApiProblem` for a problem document,
 * `TransportFailure` for a request that never landed, and the two subclasses below for the two
 * refusals this screen renders as screens rather than as messages.
 */
import { createContext, useContext } from 'react';
import { ApiProblem, hasErrorCode } from './problem';
import type { ErrorCode, Problem } from './generated';
import type {
  ArticleVideoRequest,
  OperationBody,
  OperationQuery,
  OperationResult,
  PublicId,
  StreamTimes,
  SubmitRequest,
  TransitionResult,
  VideoRule,
} from './generated';

/* ---------------------------------------------------------------------------------------------
 * The shapes, every one of them derived.
 * ------------------------------------------------------------------------------------------- */

/**
 * The mutable article as it stands. `revision` is a counter on the working-copy row and is NOT an
 * article-version id — conflating the two is the mistake ADR-0008's ruling exists to pre-empt, and
 * it is restated on the schema itself (authors-api.yaml).
 */
export type WorkingCopy = OperationResult<'getOwnArticle'>;

/** The whole-document save. `baseRevision` is the revision the client last saw. */
export type AutosaveRequest = OperationBody<'autosaveArticle'>;

/**
 * What an accepted autosave answers: everything about the working copy EXCEPT the document.
 * **The 200 does not echo `blocks`** and the 409 carries the whole copy; that asymmetry is the
 * point (authors-api.yaml).
 */
export type WorkingCopySaved = OperationResult<'autosaveArticle'>;

/** The versioned block union — text, quote, image, divider, each with a stable id. */
export type Block = WorkingCopy['blocks'][number];
export type TextBlock = Extract<Block, { kind: 'text' }>;
export type QuoteBlock = Extract<Block, { kind: 'quote' }>;
export type ImageBlock = Extract<Block, { kind: 'image' }>;
export type DividerBlock = Extract<Block, { kind: 'divider' }>;
export type VideoBlock = Extract<Block, { kind: 'video' }>;
export type BlockKind = Block['kind'];

/** Our media id plus the public rendition. An image block holds these two and never a data URL. */
export type StoredImage = NonNullable<WorkingCopy['cover']>;

/** The claim body: an object the browser has ALREADY written into its own quarantine prefix. */
export type ArticleImageClaim = OperationBody<'claimArticleImageUpload'>;

/** The structured review an author acts on. An article with no review yet gets an EMPTY record. */
export type ReviewRecord = OperationResult<'getArticleReview'>;
export type ReviewFinding = ReviewRecord['findings'][number];
export type FindingSeverity = ReviewFinding['severity'];
export type FindingDisposition = ReviewFinding['disposition'];

/** The resolved finding plus the two numbers the Resubmit button is gated on. */
export type FindingResolved = OperationResult<'resolveReviewFinding'>;

/** D21's eight limits as the submit check will apply them, read from the versioned policy. */
export type StructuralRules = OperationResult<'getStructuralRules'>;
export type StructuralRule = StructuralRules['rules'][number];
export type StructuralRuleName = StructuralRule['rule'];

export type CategoryList = OperationResult<'listEditorCategories'>;
export type EditorCategory = CategoryList['items'][number];

export type TagSuggestions = OperationResult<'suggestTags'>;
export type PreviewLink = OperationResult<'mintDraftPreviewLink'>;
export type PublishDateSuggestions = OperationResult<'getPublishDateSuggestions'>;
export type PublishDateSuggestion = PublishDateSuggestions['items'][number];

/**
 * ★ WHAT THIS OPERATION ANSWERS, AND WHAT IT DOES NOT.
 *
 * `checkArticleSlugAvailability`'s 200 is `{ available: boolean }` and nothing else (its 200 in
 * authors-api.d.ts). The richer `SlugAvailability` schema — the one carrying `reason`,
 * `suggestion` and `frozen` — belongs to the PROFILE address check and is not this operation's
 * body. So the Search pane can say whether an address is free and cannot say why it is not. A
 * FROZEN address is a refusal instead, 422 `SLUG_FROZEN` since decision-log row 219, and
 * ../../../routes/articles/editor/SearchPanel.tsx says that one, and every other refusal, on screen.
 */
export type ArticleSlugAvailability = OperationResult<'checkArticleSlugAvailability'>;

/* ---------------------------------------------------------------------------------------------
 * The interface.
 * ------------------------------------------------------------------------------------------- */

export interface EditorApi
  extends ArticleCalendarLinksApi, ArticleVersionsApi, SideReadsApi, ArticleVideoApi {
  /* ---- the working copy ------------------------------------------------------------------ */

  /**
   * The editable article as it stands. `writableBy` on the response says which of the three write
   * rules applies at the current status — the author in `draft` and `changes_requested`, NOBODY in
   * `in_review`, `scheduled`, `unpublished` and `archived`, an admin only in `published` (G19,
   * authors-api.yaml). The editor renders the read-only case rather than refusing to open.
   */
  getOwnArticle(articleId: PublicId, signal?: AbortSignal): Promise<WorkingCopy>;

  /**
   * The debounced whole-document save.
   *
   * ★ A STALE `baseRevision` THROWS `AutosaveConflict`, WHICH CARRIES THE SERVER'S WHOLE WORKING
   * COPY. That is a screen and not a message: the author's unsaved text and the server's copy are
   * shown side by side. 409, never 412 — "412 answers a precondition carried in a header; ours is
   * a field in the body" (authors-api.yaml).
   */
  autosaveArticle(articleId: PublicId, body: AutosaveRequest): Promise<WorkingCopySaved>;

  /* ---- reference data the editor MIRRORS rather than copies ------------------------------- */

  /**
   * D21's eight limits and the policy version they came from. **Fetched, never hardcoded**: this
   * operation exists so "the editor's client-side mirror renders these, so client and server cannot
   * drift" (authors-api.yaml). The prototype's 600-word minimum is not a number this platform
   * has ever held; the ruled band is 300 to 7,000 (authors-api.yaml).
   */
  getStructuralRules(
    signal?: AbortSignal,
    // `kind: 'video'` answers the limits a video is held to (no word floor, no pictures).
    query?: OperationQuery<'getStructuralRules'>,
  ): Promise<StructuralRules>;

  /** The live catalogue, ordered by `sortOrder`. An article's categories are an ARRAY, 1 to 5. */
  listEditorCategories(signal?: AbortSignal): Promise<CategoryList>;

  /** Other authors' tags, and with nothing typed MoonX's own list (`groups`). Not a closed list. */
  suggestTags(query?: OperationQuery<'suggestTags'>, signal?: AbortSignal): Promise<TagSuggestions>;

  /** Dates the events table makes interesting. Suggestions only; nothing refuses another date. */
  getPublishDateSuggestions(
    query?: OperationQuery<'getPublishDateSuggestions'>,
    signal?: AbortSignal,
  ): Promise<PublishDateSuggestions>;

  /* ---- the address and the images --------------------------------------------------------- */

  /** Whether the address is free IN THAT CATEGORY. Both query members are required. */
  checkArticleSlugAvailability(
    articleId: PublicId,
    query: OperationQuery<'checkArticleSlugAvailability'>,
    signal?: AbortSignal,
  ): Promise<ArticleSlugAvailability>;

  /**
   * Names an object the browser has already written into its own uid-keyed quarantine prefix —
   * the same mechanism the application form's two uploads use
   * (../../application/data/useUpload.ts). Nothing is ever POSTed to us, and `intakeId` IS the
   * idempotency key, so a retry reuses it.
   */
  claimArticleImageUpload(articleId: PublicId, body: ArticleImageClaim): Promise<StoredImage>;

  /** A real shareable draft link with an expiry. 201, and deliberately NO `Location`. */
  mintDraftPreviewLink(articleId: PublicId): Promise<PreviewLink>;

  /* ---- the review, and the control that unblocks a resubmit -------------------------------- */

  /**
   * The structured record. An article with no review yet gets this record with an EMPTY `findings`
   * array rather than a 404, so the notes pane has an empty state and never an error state.
   */
  getArticleReview(articleId: PublicId, signal?: AbortSignal): Promise<ReviewRecord>;

  /**
   * ★★ THE CONTROL THE PROTOTYPE DRAWS NOWHERE, AND WITHOUT WHICH `resubmitArticle` IS REFUSED
   * WITH `BLOCKING_ISSUES_UNRESOLVED`. The 200 carries `openBlockingCount` — "the count the
   * Resubmit button is gated on" (authors-api.d.ts) — so the button's state moves on
   * this response and the screen does not re-read the whole review after every resolve.
   *
   * THERE IS NO MESSAGE BODY. `resolveReviewFinding` takes no request body at all, so the pane's
   * "reply if you disagree" has nothing behind it; it is not drawn. See the OPEN list in the notes.
   */
  resolveReviewFinding(
    articleId: PublicId,
    reviewSeq: number,
    ordinal: number,
  ): Promise<FindingResolved>;

  /* ---- the two transitions ---------------------------------------------------------------- */

  /**
   * Seals a version and hands the article to moderation. **It takes no request body** — the button
   * seals what the author is looking at, which is the working copy the autosave has been writing
   * all along (authors-api.d.ts).
   *
   * ★ THE 422 RETURNS THE WHOLE FAILURE SET, NEVER THE FIRST FAILURE ALONE, so an author fixes
   * everything in one pass. It is thrown as `StructuralLimitsRefusal` below.
   *
   * Returns `TransitionResult` and not `OperationResult<'submitArticle'>`: the success here is a
   * **202** and the helper reads 200 then 201 (generated.ts). See this file's header.
   */
  submitArticle(articleId: PublicId, body?: SubmitRequest): Promise<TransitionResult>;

  /** The same shape from `changes_requested`, and refused while a blocking finding is open. */
  resubmitArticle(articleId: PublicId, body?: SubmitRequest): Promise<TransitionResult>;
}

/* ---------------------------------------------------------------------------------------------
 * The two refusals that are screens.
 * ------------------------------------------------------------------------------------------- */

/**
 * ★★ 409 `AUTOSAVE_REVISION_STALE` — "a normal outcome, not an error screen"
 * (docs/ui/04-pages.md). Somebody else saved first; usually the author's own second tab.
 *
 * `current` is the SERVER'S WHOLE WORKING COPY, carried in the body precisely so the editor can
 * show it beside the author's unsaved text WITHOUT a second round trip — "a conflict is exactly the
 * case where the client's copy is wrong and the server's is the only correct one"
 * (authors-api.yaml). Losing it here would force the re-fetch the contract removed.
 */
export class AutosaveConflict extends ApiProblem {
  /* NO `name` OVERRIDE ON EITHER SUBCLASS, and it is the base class that decides that:
     `ApiProblem` declares `override readonly name = 'ApiProblem'` (problem.ts:42), which narrows
     the property's TYPE to that one literal, so re-declaring it here is a compile error rather
     than a nicer stack trace. These two are distinguished by `instanceof` — which is also what
     keeps `describeProblem` (problem.ts:102) working on them unchanged, since both ARE an
     `ApiProblem` and carry the same `error_code`. */

  /** The server's copy. Never merged automatically; the author picks. */
  readonly current: WorkingCopy;

  constructor(problem: Problem, current: WorkingCopy) {
    super(problem);
    this.current = current;
  }
}

/** One entry of `StructuralLimitsProblem.failures`. */
export interface StructuralLimitFailure {
  /** The D21 rule code that failed. */
  readonly rule: string;
  /** The bound the rule sets. */
  readonly limit: number;
  /** What the submission carried. */
  readonly actual: number;
  /** The field the rule applies to, where the rule is field-scoped. */
  readonly field?: string | null;
}

/**
 * ★★ 422 `STRUCTURAL_LIMITS_FAILED` — the WHOLE set, never the first failure alone
 * (authors-api.d.ts). The checklist renders every entry at once.
 */
export class StructuralLimitsRefusal extends ApiProblem {
  /* See `AutosaveConflict` above for why neither subclass re-declares `name`. */

  readonly failures: readonly StructuralLimitFailure[];

  constructor(problem: Problem, failures: readonly StructuralLimitFailure[]) {
    super(problem);
    this.failures = failures;
  }
}

export function isAutosaveConflict(error: unknown): error is AutosaveConflict {
  return error instanceof AutosaveConflict;
}

export function isStructuralLimitsRefusal(error: unknown): error is StructuralLimitsRefusal {
  return error instanceof StructuralLimitsRefusal;
}

/* ---------------------------------------------------------------------------------------------
 * Reading the two extension members off a decoded problem body.
 *
 * These are runtime narrowings and not casts, and they are shared by both transports so the
 * fixture cannot be kinder than the network. A 409 whose body lost `current`, or a 422 whose body
 * lost `failures`, falls back to the plain `ApiProblem` — the screen then shows the ordinary
 * refusal rather than rendering a conflict pane with nothing in it.
 * ------------------------------------------------------------------------------------------- */

function hasBlocks(value: unknown): value is WorkingCopy {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { blocks?: unknown; revision?: unknown };
  return Array.isArray(candidate.blocks) && typeof candidate.revision === 'number';
}

/** The `current` working copy off a 409 body, or undefined when the body did not carry one. */
export function readConflictCurrent(payload: unknown): WorkingCopy | undefined {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  const current = (payload as { current?: unknown }).current;
  return hasBlocks(current) ? current : undefined;
}

function isFailure(value: unknown): value is StructuralLimitFailure {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { rule?: unknown; limit?: unknown; actual?: unknown };
  return (
    typeof candidate.rule === 'string' &&
    typeof candidate.limit === 'number' &&
    typeof candidate.actual === 'number'
  );
}

/** The `failures` array off a 422 body, or undefined when the body did not carry one. */
export function readStructuralFailures(
  payload: unknown,
): readonly StructuralLimitFailure[] | undefined {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  const failures = (payload as { failures?: unknown }).failures;
  if (!Array.isArray(failures) || !failures.every(isFailure)) {
    return undefined;
  }
  return failures;
}

/* ---------------------------------------------------------------------------------------------
 * The context.
 * ------------------------------------------------------------------------------------------- */

const EditorApiContext = createContext<EditorApi | null>(null);

export const EditorApiProvider = EditorApiContext.Provider;

export function useEditorApi(): EditorApi {
  const api = useContext(EditorApiContext);
  if (api === null) {
    throw new Error(
      'No EditorApi in context. The editor screen provides it for its own subtree — see ' +
        'routes/articles/EditorRoute.tsx, which builds the value through ' +
        'features/cabinet/api/editor-api-source.ts.',
    );
  }
  return api;
}

/* ---------------------------------------------------------------------------------------------
 * The refusal that is a detour.
 *
 * ★ 409 `TERMS_ACCEPTANCE_STALE` — `submitArticle` is refused when "the author's terms acceptance
 * is stale" (authors-api.yaml). Nothing in this editor can fix that; one click on the terms
 * interstitial can, so the screen answers it with a way there rather than with a message
 * (../../../routes/articles/editor/TermsStaleNotice.tsx). NOT A SUBCLASS like the two above: the
 * body carries no extension member, so both transports throw a plain `ApiProblem` and the code is
 * the whole of the difference. Typed `ErrorCode`, so a misspelt code does not compile.
 * ------------------------------------------------------------------------------------------- */

const TERMS_ACCEPTANCE_STALE: ErrorCode = 'TERMS_ACCEPTANCE_STALE';

export function isTermsAcceptanceStale(error: unknown): boolean {
  return hasErrorCode(error, TERMS_ACCEPTANCE_STALE);
}

/* ---------------------------------------------------------------------------------------------
 * The article's calendar events - `listArticleCalendarEvents` and `setArticleCalendarEvents`.
 *
 * ★ "THE AUTHOR CHOOSES THE EVENT WHEN PUBLISHING" (authors-api.yaml, `setArticleCalendarEvents`,
 * quoting decision-log row 105 item 7), and until wave 20 no screen offered the choice: neither
 * operation had a caller under web/authors (header-sweep area 7, F10). Both are article-scoped and
 * `capability` tier - the editor half of the calendar - so they live on this seam, and the pane
 * that calls them is ../../../routes/articles/editor/CalendarLinksPanel.tsx.
 *
 * DECLARED DOWN HERE AND FOLDED INTO `EditorApi` BY `extends`, rather than written into the
 * interface above, because lines above are cited from elsewhere and an insertion would move them.
 * ------------------------------------------------------------------------------------------- */

/**
 * The article's links: one row per event per relation, each with the event's title, start and group
 * name - and, since design 30, `groups`, the topics it is about in general, each with its name.
 */
export type ArticleCalendarLinks = OperationResult<'listArticleCalendarEvents'>;
export type ArticleCalendarLink = ArticleCalendarLinks['links'][number];
export type ArticleCalendarGroupLink = ArticleCalendarLinks['groups'][number];

/**
 * The WHOLE-SET write: every link the article should have, as `{ calendarEventId, relation }` and
 * nothing more. A link echoed back as it was read is a 400, because the write schema is strict and
 * `title` and `startsAt` are read-only (src/modules/editorial/cabinet/calendar-cabinet.schemas.ts).
 * `groups` (at most five `{ groupCode }`) is the topic set the same way; left out, the topics stay.
 */
export type ArticleCalendarLinksWrite = OperationBody<'setArticleCalendarEvents'>;

export interface ArticleCalendarLinksApi {
  /**
   * 403 `NOT_ARTICLE_OWNER` / `AUTHOR_CAPABILITY_REQUIRED` (and the guard's `AUTHOR_DISABLED`), 404
   * for an article that is not there. A link whose event has since been RETIRED is still listed.
   */
  listArticleCalendarEvents(
    articleId: PublicId,
    signal?: AbortSignal,
  ): Promise<ArticleCalendarLinks>;

  /**
   * Answers the set after the write. 400 for a relation outside `calendar_link_relation`, more
   * than ten links or five topics; 404 when the article is not there OR the set ADDS a link to an
   * event or a topic that is no longer live - one already held is kept
   * (src/modules/editorial/cabinet/article-calendar-links.service.ts, point 2).
   */
  setArticleCalendarEvents(
    articleId: PublicId,
    body: ArticleCalendarLinksWrite,
  ): Promise<ArticleCalendarLinks>;
}

/* ---------------------------------------------------------------------------------------------
 * The article's stored versions - `listArticleVersions`, `getArticleVersion`, `saveArticleVersion`.
 *
 * ★ SERVED SINCE 2026-09-20 AND CALLED BY NOTHING UNTIL WAVE 25b (register R-017, lens findings
 * C2-06 and U1-05). The editor's History pane (../../../routes/articles/editor/HistoryPanel.tsx)
 * lists them, opens one read-only and keeps the working copy as a new one. **There is no restore
 * route** - "that was the cut" (authors-api.yaml, `listArticleVersions`) - so none is declared.
 *
 * A VERSION IS ADDRESSED BY ITS AUTHOR-VISIBLE REVISION NUMBER, never by a row id, and a number
 * alone names a row only together with a language: translations are version rows too, so
 * `getArticleVersion` takes the entry's `languageCode` for a translation and nothing for a source
 * row, whose default is "the article's source language".
 *
 * Folded into `EditorApi` by `extends`, as the calendar pair is, so no cited line above moves.
 * ------------------------------------------------------------------------------------------- */

export type VersionList = OperationResult<'listArticleVersions'>;
export type ArticleVersionSummary = VersionList['items'][number];
export type ArticleVersion = OperationResult<'getArticleVersion'>;

export interface ArticleVersionsApi {
  /** Newest first, and NOT paged: the operation declares no cursor and a `?cursor=` is a 400. */
  listArticleVersions(articleId: PublicId, signal?: AbortSignal): Promise<VersionList>;

  /** One sealed snapshot with its blocks. Immutable. 404 for no such revision or language row. */
  getArticleVersion(
    articleId: PublicId,
    revisionNo: number,
    query?: OperationQuery<'getArticleVersion'>,
    signal?: AbortSignal,
  ): Promise<ArticleVersion>;

  /**
   * ★ SEALS THE WORKING COPY AS IT STANDS ON THE SERVER, AND TAKES NO BODY. The button seals what
   * the author is looking at only if the last autosave has landed, so the screen saves first -
   * as Send does. 201 with the entry the list will show; 403 `ARTICLE_NOT_WRITABLE` outside
   * `draft` and `changes_requested`. Not a transition, and no structural limit is checked.
   */
  saveArticleVersion(articleId: PublicId): Promise<ArticleVersionSummary>;
}

/* ---------------------------------------------------------------------------------------------
 * The author's own profile - `getOwnProfile` - for the byline card beside the draft (R-033).
 *
 * THE SAME OPERATION THE APPLICATION FLOW READS (../../application/api/http.ts), held on this seam
 * too so the editor keeps one API in its context and one fixture-or-network switch. The response
 * carries the PRIVATE block (contact e-mail, rate); the card draws the public byline only.
 * ------------------------------------------------------------------------------------------- */

export type OwnProfile = OperationResult<'getOwnProfile'>;

export interface OwnProfileApi {
  getOwnProfile(signal?: AbortSignal): Promise<OwnProfile>;
}

/* ---------------------------------------------------------------------------------------------
 * The languages an article may be written in - `listSourceLanguages` (wave 26, register R-003).
 *
 * `translation_policy_source_language` at the policy in force, each with the catalogue's name, and
 * the same set the autosave checks a `sourceLanguage` change against - so the Setup pane's picker
 * (../../../routes/articles/editor/SetupPanel.tsx) offers what a save accepts rather than D24.1's
 * four written into this app.
 *
 * FOLDED INTO `EditorApi` THROUGH `SideReadsApi` BELOW, on the one line that already named
 * `OwnProfileApi`: a fourth name there would be a line prettier wraps, and the lines under it are
 * cited from elsewhere.
 * ------------------------------------------------------------------------------------------- */

export type SourceLanguageList = OperationResult<'listSourceLanguages'>;
export type SourceLanguage = SourceLanguageList['items'][number];

export interface SourceLanguagesApi {
  /** In the catalogue's order. EMPTY when no translation policy is in force - then no change saves. */
  listSourceLanguages(signal?: AbortSignal): Promise<SourceLanguageList>;
}

/** The two reads beside the draft that are not the article's own: the byline and the languages. */
export interface SideReadsApi extends OwnProfileApi, SourceLanguagesApi {}

/* ---------------------------------------------------------------------------------------------
 * MoonX's own tag list - `suggestTags`'s `groups` (design 24, T6).
 *
 * Asked with nothing typed, `suggestTags` answers MoonX's list beside other authors' tags: each
 * group a label and its tags, in the order the author should see them. Asked with `q`, it answers
 * only `items`. The live server sends no `groups` until the pause applies the list's migration, so
 * a screen reads an absent `groups` as an empty list. Declared at the foot so no cited line above
 * moves (`claimArticleImageUpload` is cited by number).
 * ------------------------------------------------------------------------------------------- */

export type TagSuggestionGroup = NonNullable<TagSuggestions['groups']>[number];

/* ---------------------------------------------------------------------------------------------
 * A video's video (v1.0.29) - `getArticleVideo`, `setArticleVideo`, `removeArticleVideo`,
 * `setVideoCover` and `setVideoDraftConfirmations`. A video is a kind of article (`WorkingCopy.kind`): the same draft, autosave and
 * submit, with this video in place of pictures. Folded into `EditorApi` by `extends`. A live
 * stream (1.2.0) is such a video with a YouTube link and its planned times (`setStreamTimes`).
 * ------------------------------------------------------------------------------------------- */

export type {
  ArticleVideoRequest,
  StreamTimes,
  SubmitRequest,
  VideoRule,
  WorkingStream,
} from './generated';

/** The video as the platform holds it; `getArticleVideo` adds short-lived addresses to play it. */
export type ArticleVideo = OperationResult<'getArticleVideo'>;
export type VideoState = ArticleVideo['state'];
export type VideoOrientation = NonNullable<ArticleVideo['orientation']>;
/** A frame of the video (0 to 7), or the YouTube still. */
export type VideoCoverRequest = OperationBody<'setVideoCover'>;
/** A video block's video, set before the block is in the document (`setArticleVideoBlock`). */
export type ArticleVideoBlockRequest = OperationBody<'setArticleVideoBlock'>;
/** The rules ticked on a video draft, as kept (`setVideoDraftConfirmations`). */
export type VideoDraftConfirmations = OperationResult<'setVideoDraftConfirmations'>;

/**
 * The rules of "Video guidelines for MoonX authors", in the guidelines' order. A video's submit
 * must confirm every one (`VIDEO_CONFIRMATIONS_REQUIRED`).
 */
export const VIDEO_RULES = [
  'own_rights',
  'music_licensed',
  'consent_no_children',
  'topic_fits',
  'nothing_harmful',
  'no_promises',
  'ai_disclosed',
  'no_ads',
] as const satisfies readonly VideoRule[];

/**
 * Why the media worker refused an upload (`ArticleVideo.failureCode`). The contract types it as a
 * plain string, so a code not listed here is still possible and is worded generally.
 */
export const VIDEO_FAILURE_CODES = [
  'too_short',
  'too_long',
  'too_small',
  'too_large',
  'wrong_shape',
  'unreadable',
  'conversion_failed',
  'upload_missing',
] as const;
export type VideoFailureCode = (typeof VIDEO_FAILURE_CODES)[number];

/**
 * The largest video file: 3 GB, and `<` - the storage rules' own test, which the server's
 * `setArticleVideo` mirrors. Not in the contract, so it is stated once here for both desks.
 */
export const VIDEO_MAX_BYTES = 3 * 1024 * 1024 * 1024;

/** The three refusals only a video meets. */
export const VIDEO_ERROR_CODE = {
  accessRequired: 'VIDEO_ACCESS_REQUIRED',
  videoRequired: 'VIDEO_REQUIRED',
  confirmationsRequired: 'VIDEO_CONFIRMATIONS_REQUIRED',
} as const satisfies Readonly<Record<string, ErrorCode>>;

/**
 * A live stream's two (1.2.0): no stream access, and a submit whose planned times are missing or
 * whose start has passed.
 */
export const STREAM_ERROR_CODE = {
  accessRequired: 'STREAM_ACCESS_REQUIRED',
  timesRequired: 'STREAM_TIMES_REQUIRED',
} as const satisfies Readonly<Record<string, ErrorCode>>;

export interface ArticleVideoApi {
  /**
   * The video, with `playbackUrl` and `frameUrls` once an upload is `ready` - signed addresses good
   * for about an hour, so the editor asks again when they stop working. 404 when there is none.
   */
  getArticleVideo(articleId: PublicId, signal?: AbortSignal): Promise<ArticleVideo>;

  /**
   * Attach or replace the video while the author may write the draft: a YouTube link (ready at
   * once), or a file already written into the caller's quarantine under `intakeId`, which the media
   * worker then checks and converts (`uploaded`, `processing`, then `ready` or `failed`). 422 for a
   * link that is not one YouTube video, or a file over the limit.
   */
  setArticleVideo(articleId: PublicId, body: ArticleVideoRequest): Promise<ArticleVideo>;

  /** 204. Removing a draft's absent video is the same answer. */
  removeArticleVideo(articleId: PublicId): Promise<void>;

  /** A video block's video, with `playbackUrl` once an upload is `ready`. 404 when it has none. */
  getArticleVideoBlock(
    articleId: PublicId,
    blockId: string,
    signal?: AbortSignal,
  ): Promise<ArticleVideo>;

  /**
   * Set or replace a video block's video, the guidelines ticked: a YouTube link, or a file already
   * in the caller's quarantine, as `setArticleVideo`. The block goes into the document afterwards,
   * by autosave. 422 past `MAX_VIDEO_BLOCKS`.
   */
  setArticleVideoBlock(
    articleId: PublicId,
    blockId: string,
    body: ArticleVideoBlockRequest,
  ): Promise<ArticleVideo>;

  /**
   * A frame of a ready upload, or a YouTube video's still, stored as the video's cover (it keeps a
   * vertical cover vertical). The editor then names its `fileId` by autosave, as after an upload.
   */
  setVideoCover(articleId: PublicId, body: VideoCoverRequest): Promise<StoredImage>;

  /**
   * Every rule ticked on the draft right now, sent on each tick so leaving the page keeps them;
   * `WorkingCopy.videoConfirmations` gives them back. Allowed while the author may write the draft.
   */
  setVideoDraftConfirmations(
    articleId: PublicId,
    body: VideoDraftConfirmations,
  ): Promise<VideoDraftConfirmations>;

  /**
   * A live stream draft's planned start and end (1.2.0), the end after the start, while the author
   * may write the draft; `WorkingCopy.stream` gives them back. 422 for a video that is not a
   * stream, 403 `STREAM_ACCESS_REQUIRED` without stream access.
   */
  setStreamTimes(articleId: PublicId, body: StreamTimes): Promise<StreamTimes>;
}
